import {
  SISTER_SEARCHER_PERFORMANCE_METRICS,
  SISTER_SEARCHER_PERFORMANCE_SCOPES,
} from "../constants/sister-searcher.constants";
import { SisterSearcherPerformance } from "../models/sister-searcher.models";
import {
  formatSisterSearcherPerformancePoint,
  formatSisterSearcherValue,
} from "../utils/sister-searcher-format.utils";

interface SisterSearcherPerformanceTableProps {
  rows: SisterSearcherPerformance[];
  dataTest: string;
}

// Mirrors the PBIP Performance bookmark columns and keeps all four operating scopes visible in one horizontal table.
// Nullable normalized consumption and unavailable thin-report facts intentionally render as em dashes.
const SisterSearcherPerformanceTable = ({ rows, dataTest }: SisterSearcherPerformanceTableProps) => (
  <div className="max-h-[620px] overflow-auto" data-test={dataTest}>
    <table className="min-w-[2700px] border-separate border-spacing-0 text-[11px]">
      <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
        <tr>
          {[
            "IMO",
            "Vessel Name",
            "Perf Data Source",
            "Evaluated",
            "Scrubber",
            "In IMOS",
            "Last Fixture",
          ].map((label) => (
            <th
              key={label}
              rowSpan={2}
              className="whitespace-nowrap border-b border-r border-gray-200 px-3 py-3 font-semibold dark:border-gray-700"
            >
              {label}
            </th>
          ))}
          {SISTER_SEARCHER_PERFORMANCE_SCOPES.map((scope) => (
            <th
              key={scope.key}
              colSpan={scope.includeNormalizedConsumption ? 5 : 4}
              className="border-b border-r border-gray-200 px-3 py-2 text-center font-semibold text-green dark:border-gray-700"
            >
              {scope.label}
            </th>
          ))}
        </tr>
        <tr>
          {SISTER_SEARCHER_PERFORMANCE_SCOPES.flatMap((scope) =>
            SISTER_SEARCHER_PERFORMANCE_METRICS.filter(
              (metric) => metric.key !== "normalizedConsumption" || scope.includeNormalizedConsumption,
            ).map((metric) => (
              <th
                key={`${scope.key}-${metric.key}`}
                className="whitespace-nowrap border-b border-r border-gray-200 px-3 py-2 font-semibold dark:border-gray-700"
              >
                {metric.key === "normalizedConsumption"
                  ? scope.normalizedConsumptionLabel
                  : metric.label}
              </th>
            )),
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.imo}
            className="text-gray-700 odd:bg-white even:bg-gray-50/60 hover:bg-primary-10 dark:text-gray-200 dark:odd:bg-gray-800 dark:even:bg-gray-800/70 dark:hover:bg-teal-950/50"
          >
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 font-mono text-green dark:border-gray-700">{row.imo}</td>
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100">{row.vesselName}</td>
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 dark:border-gray-700">{formatSisterSearcherValue(row.perfDataSource)}</td>
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 dark:border-gray-700">{formatSisterSearcherValue(row.evaluated)}</td>
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 dark:border-gray-700">{formatSisterSearcherValue(row.scrubber)}</td>
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 dark:border-gray-700">{formatSisterSearcherValue(row.inImos)}</td>
            <td className="whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 dark:border-gray-700">{formatSisterSearcherValue(row.lastFixture)}</td>
            {SISTER_SEARCHER_PERFORMANCE_SCOPES.flatMap((scope) =>
              SISTER_SEARCHER_PERFORMANCE_METRICS.filter(
                (metric) => metric.key !== "normalizedConsumption" || scope.includeNormalizedConsumption,
              ).map((metric) => (
                <td
                  key={`${row.imo}-${scope.key}-${metric.key}`}
                  className="max-w-[240px] whitespace-nowrap border-b border-r border-gray-100 px-3 py-2 dark:border-gray-700"
                >
                  {formatSisterSearcherPerformancePoint(row[scope.key], metric.key)}
                </td>
              )),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SisterSearcherPerformanceTable;
